import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Profileinfo.css";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import API from "../config";

function ProfileInfo() {
  const navigate = useNavigate();
  const { lang } = useLanguage();

  /* ================= STATE ================= */
  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    address: "",
    birthday: "",
    gender: "",
    country: "",
    phonenumber: "",
    idcardorpassport: ""
  });

  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true); // สำหรับรอโหลด profile

  /* ================= REFS (for scroll) ================= */
  const fieldRefs = {
    firstname: useRef(null),
    lastname: useRef(null),
    address: useRef(null),
    birthday: useRef(null),
    gender: useRef(null),
    country: useRef(null),
    phonenumber: useRef(null),
    idcardorpassport: useRef(null)
  };

  /* ================= HANDLER ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* ================= VALIDATE ================= */
  const validate = () => {
    let err = {};

    if (!form.firstname) err.firstname = "Required";
    if (!form.lastname) err.lastname = "Required";
    if (!form.address) err.address = "Required";
    if (!form.birthday) err.birthday = "Required";
    if (!form.gender) err.gender = "Required";
    if (!form.country) err.country = "Required";

    // 🇹🇭 Thai phone only
    if (!form.phonenumber) {
      err.phonenumber = "Required";
    } else if (!/^0[689]\d{8}$/.test(form.phonenumber)) {
      err.phonenumber = "Enter Thai phone number (0xxxxxxxxx)";
    }

    if (!form.idcardorpassport) err.idcardorpassport = "Required";

    setError(err);

    // scroll ไปช่อง error แรก
    const firstErrorKey = Object.keys(err)[0];
    if (firstErrorKey && fieldRefs[firstErrorKey]?.current) {
      fieldRefs[firstErrorKey].current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      fieldRefs[firstErrorKey].current.focus();
    }

    return Object.keys(err).length === 0;
  };

  /* ================= LOAD PROFILE ================= */
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const res = await fetch(`${API}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        }

        const data = await res.json();

        setForm({
          firstname: data.firstname || "",
          lastname: data.lastname || "",
          address: data.address || "",
          birthday: data.birthday ? data.birthday.slice(0, 10) : "",
          gender: data.gender || "",
          country: data.country || "",
          phonenumber: data.phonenumber || "",
          idcardorpassport: data.idcardorpassport || ""
        });
      } catch (err) {
        console.error(err);
        alert("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please login again");
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(`${API}/api/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Save failed");

      alert(data.message);
      navigate("/rooms"); // redirect หลัง save
    } catch (err) {
      console.error(err);
      alert(err.message || "Save failed");
    }
  };

  /* ================= DISABLE SAVE ================= */
  const isFormComplete =
    form.firstname &&
    form.lastname &&
    form.address &&
    form.birthday &&
    form.gender &&
    form.country &&
    form.idcardorpassport &&
    /^0[689]\d{8}$/.test(form.phonenumber);

  if (loading) return <p style={{ padding: 40 }}>{t(lang, "loading")}</p>;

  /* ================= UI ================= */
  return (
    <div className="profile-bg">
      <div className="profile-wrapper">
        <div className="profile-card">
          <h4>{t(lang, "profileInfoTitle")}</h4>

          <div className="profile-grid">

            <div className="profile-group">
              <label>{t(lang, "firstnameLabel")}</label>
              <input
                name="firstname"
                ref={fieldRefs.firstname}
                value={form.firstname}
                onChange={handleChange}
              />
              <div className="profile-error">{error.firstname}</div>
            </div>

            <div className="profile-group">
              <label>{t(lang, "lastnameLabel")}</label>
              <input
                name="lastname"
                ref={fieldRefs.lastname}
                value={form.lastname}
                onChange={handleChange}
              />
              <div className="profile-error">{error.lastname}</div>
            </div>

            <div className="profile-group full">
              <label>{t(lang, "addressLabel")}</label>
              <input
                name="address"
                ref={fieldRefs.address}
                value={form.address}
                onChange={handleChange}
              />
              <div className="profile-error">{error.address}</div>
            </div>

            <div className="profile-group">
              <label>{t(lang, "birthdayLabel")}</label>
              <input
                type="date"
                name="birthday"
                ref={fieldRefs.birthday}
                value={form.birthday}
                onChange={handleChange}
              />
              <div className="profile-error">{error.birthday}</div>
            </div>

            <div className="profile-group">
              <label>{t(lang, "genderLabel")}</label>
              <select
                name="gender"
                ref={fieldRefs.gender}
                value={form.gender}
                onChange={handleChange}
              >
                <option value="">{t(lang, "genderSelect")}</option>
                <option value="male">{t(lang, "male")}</option>
                <option value="female">{t(lang, "female")}</option>
              </select>
              <div className="profile-error">{error.gender}</div>
            </div>

            <div className="profile-group">
              <label>{t(lang, "countryLabel")}</label>
              <input
                name="country"
                ref={fieldRefs.country}
                value={form.country}
                onChange={handleChange}
              />
              <div className="profile-error">{error.country}</div>
            </div>

            <div className="profile-group">
              <label>{t(lang, "phoneThaiLabel")}</label>
              <input
                name="phonenumber"
                ref={fieldRefs.phonenumber}
                placeholder="0991234567"
                value={form.phonenumber}
                onChange={handleChange}
              />
              <div className="profile-error">{error.phonenumber}</div>
            </div>

            <div className="profile-group full">
              <label>{t(lang, "idCardLabel")}</label>
              <input
                name="idcardorpassport"
                ref={fieldRefs.idcardorpassport}
                value={form.idcardorpassport}
                onChange={handleChange}
              />
              <div className="profile-error">{error.idcardorpassport}</div>
            </div>

          </div>

          <button
            className="profile-btn"
            onClick={handleSubmit}
            disabled={!isFormComplete}
          >
            {t(lang, "saveBtn")}
          </button>

        </div>
      </div>
    </div>
  );
}

export default ProfileInfo;
